/**
 * Panel Component - Container for other components
 */

import { UIComponent, UIStyle } from './UIComponent';

export interface PanelStyle extends UIStyle {
  // Panel specific
  title?: string;
  titleColor?: string;
  titleFontSize?: number;
  showCloseButton?: boolean;
}

export class Panel extends UIComponent {
  protected style: PanelStyle;
  private title?: string;
  public onClose?: () => void;

  constructor(style: Partial<PanelStyle> = {}) {
    super(style);
    this.style = { ...this.getDefaultStyle(), ...style } as PanelStyle;
    this.title = style.title;
  }

  protected getDefaultStyle(): PanelStyle {
    return {
      ...super.getDefaultStyle(),
      backgroundColor: '#1a1510',
      borderColor: '#4a3f35',
      borderWidth: 2,
      borderRadius: 10,
      padding: 20,
      gap: 10,
      titleColor: '#d4af37',
      titleFontSize: 24,
      showCloseButton: true,
    };
  }

  measureSize(ctx: CanvasRenderingContext2D): { width: number; height: number } {
    const padding = this.getPadding();
    let contentWidth = 0;
    let contentHeight = 0;

    // Title height
    if (this.title) {
      contentHeight += (this.style.titleFontSize || 24) + 15;
    }

    // Measure children
    const gap = this.style.gap || 0;
    const direction = this.style.flexDirection || 'column';

    for (let i = 0; i < this.children.length; i++) {
      const childSize = this.children[i].measureSize(ctx);

      if (direction === 'column') {
        contentWidth = Math.max(contentWidth, childSize.width);
        contentHeight += childSize.height + (i > 0 ? gap : 0);
      } else {
        contentWidth += childSize.width + (i > 0 ? gap : 0);
        contentHeight = Math.max(contentHeight, childSize.height);
      }
    }

    return {
      width: contentWidth + padding.left + padding.right,
      height: contentHeight + padding.top + padding.bottom,
    };
  }

  protected layoutChildren(ctx: CanvasRenderingContext2D): void {
    if (this.children.length === 0) return;

    const padding = this.getPadding();
    const gap = this.style.gap || 0;
    const direction = this.style.flexDirection || 'column';

    // Account for title
    let titleOffset = 0;
    if (this.title) {
      titleOffset = (this.style.titleFontSize || 24) + 15;
    }

    const contentX = this.bounds.x + padding.left;
    const contentY = this.bounds.y + padding.top + titleOffset;
    const contentWidth = this.bounds.width - padding.left - padding.right;
    const contentHeight = this.bounds.height - padding.top - padding.bottom - titleOffset;

    if (direction === 'column') {
      let y = contentY;
      for (let i = 0; i < this.children.length; i++) {
        const child = this.children[i];
        const size = child.measureSize(ctx);

        let x = contentX;
        let childWidth = size.width;

        if (this.style.alignItems === 'center') {
          x = contentX + (contentWidth - size.width) / 2;
        } else if (this.style.alignItems === 'end') {
          x = contentX + contentWidth - size.width;
        } else if (this.style.alignItems === 'stretch') {
          childWidth = contentWidth;
        }

        child.layout(ctx, { x, y, width: childWidth, height: size.height });
        y += child.getBounds().height + (i < this.children.length - 1 ? gap : 0);
      }
    } else {
      let x = contentX;
      for (let i = 0; i < this.children.length; i++) {
        const child = this.children[i];
        const size = child.measureSize(ctx);

        let y = contentY;
        let childHeight = size.height;

        if (this.style.alignItems === 'center') {
          y = contentY + (contentHeight - size.height) / 2;
        } else if (this.style.alignItems === 'end') {
          y = contentY + contentHeight - size.height;
        } else if (this.style.alignItems === 'stretch') {
          childHeight = contentHeight;
        }

        child.layout(ctx, { x, y, width: size.width, height: childHeight });
        x += child.getBounds().width + (i < this.children.length - 1 ? gap : 0);
      }
    }
  }

  protected renderContent(ctx: CanvasRenderingContext2D): void {
    const padding = this.getPadding();

    // Render title
    if (this.title) {
      ctx.fillStyle = this.style.titleColor || '#d4af37';
      ctx.font = `bold ${this.style.titleFontSize || 24}px ${this.style.fontFamily || 'Arial'}`;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'top';
      ctx.fillText(this.title, this.bounds.x + padding.left, this.bounds.y + padding.top);
    }

    // Render close button
    if (this.style.showCloseButton) {
      const btnSize = 25;
      const btnX = this.bounds.x + this.bounds.width - padding.right - btnSize + 5;
      const btnY = this.bounds.y + padding.top - 5;

      ctx.fillStyle = '#3d3228';
      this.drawRoundedRect(ctx, btnX, btnY, btnSize, btnSize, 5);
      ctx.fill();

      ctx.strokeStyle = '#8b7355';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(btnX + 7, btnY + 7);
      ctx.lineTo(btnX + 18, btnY + 18);
      ctx.moveTo(btnX + 18, btnY + 7);
      ctx.lineTo(btnX + 7, btnY + 18);
      ctx.stroke();
    }
  }

  handleMouseEvent(e: import('./UIComponent').MouseEvent): boolean {
    // Check close button
    if (this.style.showCloseButton && e.type === 'down' && e.button === 'left') {
      const padding = this.getPadding();
      const btnSize = 25;
      const btnX = this.bounds.x + this.bounds.width - padding.right - btnSize + 5;
      const btnY = this.bounds.y + padding.top - 5;

      if (e.x >= btnX && e.x <= btnX + btnSize && e.y >= btnY && e.y <= btnY + btnSize) {
        if (this.onClose) {
          this.onClose();
        }
        return true;
      }
    }

    return super.handleMouseEvent(e);
  }

  setTitle(title: string): void {
    this.title = title;
  }
}
